import React from "react";
import { motion } from "framer-motion";
import { DOCS_URL, DISCORD_URL } from "../../constants/branding";

const footerLinks = [
  { label: "About", targetId: "services" },
  { label: "Pricing", targetId: "pricing" },
  { label: "Docs", externalHref: DOCS_URL },
];

const NAVBAR_OFFSET = 72;

const LandingFooter = () => {
  const handleScrollToSection = (event, targetId) => {
    event.preventDefault();
    const section = document.getElementById(targetId);
    if (!section) {
      return;
    }

    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: Math.max(0, sectionTop - NAVBAR_OFFSET),
      behavior: "smooth",
    });
  };

  const scrollToPricing = (event) => {
    handleScrollToSection(event, "pricing");
  };

  return (
    <motion.footer
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-full bg-landing-section"
    >
      <div className="mx-auto w-full max-w-[1440px] px-6 pb-12 pt-16 md:pb-16 md:pt-20">
        {/* CTA: headline left, actions right */}
        <div className="flex flex-col gap-10 md:flex-row md:items-center md:justify-between md:gap-12">
          <h2 className="font-title max-w-[620px] text-[32px] font-bold leading-[1.12] tracking-tight text-white sm:text-[38px] md:text-[42px]">
            Ready for take off?
            <br />
            Starter Node plan is for you
          </h2>

          <div className="flex flex-shrink-0 flex-wrap items-center gap-4 md:justify-end">
            <a
              href="/#/signup"
              className="font-secondary inline-flex py-4 min-w-[140px] items-center justify-center gap-2 rounded-sm bg-primary px-7 text-lg font-semibold text-[#0a0a0a] transition-colors hover:bg-primary-hover"
            >
              Get Started
              <span aria-hidden="true" className="text-lg leading-none">
                →
              </span>
            </a>
            <a
              href="#pricing"
              onClick={scrollToPricing}
              className="font-secondary inline-flex py-4 items-center justify-center rounded-sm bg-transparent px-7 text-lg font-semibold text-white transition-colors hover:border-white/40 hover:bg-white/[0.06]"
            >
              Learn more
            </a>
          </div>
        </div>

        {/* Nav + social + copyright */}
        <div className="mt-16 flex flex-col items-center gap-10 border-t border-white/[0.06] pt-14 md:mt-20 md:pt-16">
          <nav
            className="font-secondary flex flex-wrap justify-center gap-x-10 gap-y-3 text-md text-white sm:gap-x-12"
            aria-label="Footer"
          >
            {footerLinks.map(({ label, targetId, externalHref }) => {
              if (externalHref) {
                return (
                  <a
                    key={label}
                    href={externalHref}
                    target="_blank"
                    rel="noreferrer"
                    className="transition-colors hover:text-white/85"
                  >
                    {label}
                  </a>
                );
              }
              return (
                <a
                  key={label}
                  href={`#${targetId}`}
                  onClick={(event) => handleScrollToSection(event, targetId)}
                  className="transition-colors hover:text-white/85"
                >
                  {label}
                </a>
              );
            })}
          </nav>

          <div className="flex items-center gap-10 text-white">
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Discord"
              className="opacity-90 transition-opacity hover:opacity-100"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.211.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0 12.63 12.63 0 0 0-.617-1.25.077.077 0 0 0-.079-.036A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.099.246.197.373.291a.077.077 0 0 1-.006.128 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.094 2.157 2.42 0 1.333-.956 2.418-2.157 2.418Zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.094 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z" />
              </svg>
            </a>
            <a
              href="https://x.com"
              target="_blank"
              rel="noreferrer"
              aria-label="X"
              className="opacity-90 transition-opacity hover:opacity-100"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                <path d="M18.9 2H22l-6.8 7.8L23 22h-6.1l-4.8-6.2L6.7 22H3.6l7.3-8.5L1 2h6.2l4.4 5.8L18.9 2Zm-1.1 18h1.7L6.3 3.9H4.4L17.8 20Z" />
              </svg>
            </a>
            <a
              href="https://github.com"
              target="_blank"
              rel="noreferrer"
              aria-label="GitHub"
              className="opacity-90 transition-opacity hover:opacity-100"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                <path d="M12 .5a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2.2c-3.3.7-4-1.6-4-1.6-.5-1.3-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.9 1.2 1.9 1.2 1.1 1.9 2.9 1.4 3.6 1.1.1-.8.4-1.4.8-1.7-2.7-.3-5.5-1.3-5.5-6 0-1.3.5-2.4 1.2-3.2-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.3 1.2a11.3 11.3 0 0 1 6 0c2.3-1.6 3.3-1.2 3.3-1.2.6 1.7.2 2.9.1 3.2.8.9 1.2 2 1.2 3.2 0 4.7-2.8 5.7-5.5 6 .4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .5Z" />
              </svg>
            </a>
          </div>

          <p className="font-secondary text-center text-[12px] text-gray-500">
            © 2026 Nodefleet, LLC. All rights reserved.
          </p>
        </div>
      </div>
    </motion.footer>
  );
};

export default LandingFooter;
